class ProductRelatedArticlesSlider extends HTMLElement {
  constructor() {
    super();
    this.slider = null;
    this.flickityInstance = null;
  }

  connectedCallback() {
    this._lastResizeWidth = window.innerWidth;
    this._onResize = this._onResize.bind(this);
    this.slider = this.querySelector('.product-related-articles__slider');
    if (!this.slider) return;

    this._waitForFlickity(() => this._init());
    window.addEventListener('resize', this._onResize);
  }

  disconnectedCallback() {
    clearTimeout(this._resizeTimer);
    clearInterval(this._initTimer);
    window.removeEventListener('resize', this._onResize);
    if (this._lazyListener) {
      document.removeEventListener('lazyloaded', this._lazyListener);
      this._lazyListener = null;
    }
    this._destroyFlickity();
  }

  _waitForFlickity(callback) {
    if (typeof Flickity !== 'undefined') {
      callback();
      return;
    }

    let attempts = 0;
    const check = () => {
      attempts += 1;
      if (typeof Flickity !== 'undefined') {
        callback();
        return;
      }
      if (attempts < 100) requestAnimationFrame(check);
    };
    requestAnimationFrame(check);
  }

  _getFlickityOptions() {
    const defaults = {
      draggable: true,
      pageDots: false,
      cellAlign: 'left',
      wrapAround: false,
      contain: true,
      prevNextButtons: true,
      percentPosition: false,
      lazyLoad: false
    };

    const optionsAttr = this.slider.dataset.flickityOptions;
    if (!optionsAttr) return defaults;

    try {
      return Object.assign(defaults, JSON.parse(optionsAttr));
    } catch (_e) {
      return defaults;
    }
  }

  _shouldUseSlider() {
    return window.innerWidth >= 990;
  }

  _onResize() {
    const width = window.innerWidth;
    if (width === this._lastResizeWidth) return;
    this._lastResizeWidth = width;

    clearTimeout(this._resizeTimer);
    this._resizeTimer = setTimeout(() => this._init(), 150);
  }

  _init() {
    if (!this.slider) return;

    if (!this._shouldUseSlider()) {
      this._resetMobile();
      return;
    }

    const runInit = () => {
      if (!this._shouldUseSlider()) {
        this._resetMobile();
        return;
      }
      this.slider.classList.remove('product-related-articles__track--stacked');
      void this.slider.offsetWidth;
      this._initFlickity();
    };

    runInit();
    requestAnimationFrame(() => requestAnimationFrame(runInit));
    window.addEventListener('load', runInit, { once: true });

    clearInterval(this._initTimer);
    let attempts = 0;
    this._initTimer = setInterval(() => {
      attempts += 1;
      if (attempts > 20 || this.slider.classList.contains('flickity-enabled')) {
        clearInterval(this._initTimer);
        return;
      }
      runInit();
    }, 250);
  }

  _resetMobile() {
    this._destroyFlickity();
    this.slider.classList.remove('flickity-enabled');
    this.slider.classList.add('product-related-articles__track--stacked');

    this.slider.querySelectorAll('.flickity-prev-next-button').forEach((btn) => {
      btn.remove();
    });

    const viewport = this.slider.querySelector('.flickity-viewport');
    if (viewport) {
      const track = viewport.querySelector('.flickity-slider');
      if (track) {
        while (track.firstChild) {
          this.slider.appendChild(track.firstChild);
        }
      }
      viewport.remove();
    }

    this.slider.querySelectorAll('.grid-item').forEach((cell) => {
      cell.removeAttribute('style');
      cell.classList.remove('is-selected');
    });

    this.slider.style.removeProperty('height');
    this.slider.style.removeProperty('min-height');
  }

  _destroyFlickity() {
    if (!this.flickityInstance) return;
    try {
      this.flickityInstance.destroy();
    } catch (_) {}
    this.flickityInstance = null;
  }

  _initFlickity() {
    if (typeof Flickity === 'undefined') return;

    this._destroyFlickity();
    const options = this._getFlickityOptions();
    this._isWrapAround = Boolean(options.wrapAround);
    this.flickityInstance = new Flickity(this.slider, options);
    this._bindLayoutRefresh();
  }

  _getScrollEdges() {
    const sliderViewport = this.slider.querySelector('.flickity-viewport');
    const sliderCells = this.flickityInstance.getCellElements();

    if (!sliderViewport || !sliderCells.length) {
      return { atStart: true, atEnd: true };
    }

    const viewportRectangle = sliderViewport.getBoundingClientRect();
    const firstCellRectangle = sliderCells[0].getBoundingClientRect();
    const lastCellRectangle = sliderCells[sliderCells.length - 1].getBoundingClientRect();

    return {
      atStart: firstCellRectangle.left >= viewportRectangle.left - 2,
      atEnd: lastCellRectangle.right <= viewportRectangle.right + 2
    };
  }

  _clampToScrollEnd() {
    if (this._isWrapAround) return;
    const flickityInstance = this.flickityInstance;
    const { atEnd } = this._getScrollEdges();
    if (!atEnd) return;

    const maximumScroll = Math.max(
      0,
      (flickityInstance.slideableWidth || 0) - (flickityInstance.size.innerWidth || 0)
    );
    if (Math.abs(flickityInstance.x || 0) <= maximumScroll + 2) return;

    const lastCellIndex = flickityInstance.cells.length - 1;
    flickityInstance.select(lastCellIndex, false, true);
  }

  _bindLayoutRefresh() {
    const refreshSlider = () => {
      if (!this.flickityInstance) return;
      try {
        this.flickityInstance.resize();
        this._updateNavButtons();
      } catch (_) {}
    };

    const updateNavButtons = () => {
      if (!this.flickityInstance) return;
      try {
        this._updateNavButtons();
      } catch (_) {}
    };

    this.flickityInstance.on('ready', refreshSlider);
    this.flickityInstance.on('settle', updateNavButtons);
    this.flickityInstance.on('dragEnd', updateNavButtons);
    this.flickityInstance.on('select', updateNavButtons);

    requestAnimationFrame(() => {
      requestAnimationFrame(refreshSlider);
    });

    setTimeout(refreshSlider, 100);
    setTimeout(refreshSlider, 400);

    this.slider.querySelectorAll('img').forEach((img) => {
      if (img.complete) return;
      img.addEventListener('load', refreshSlider, { once: true });
    });

    if (!this._lazyListener) {
      this._lazyListener = (event) => {
        if (this.slider.contains(event.target)) refreshSlider();
      };
      document.addEventListener('lazyloaded', this._lazyListener);
    }
  }

  _updateNavButtons() {
    const prevButton = this.flickityInstance.previousButton && this.flickityInstance.previousButton.element;
    const nextButton = this.flickityInstance.nextButton && this.flickityInstance.nextButton.element;
    if (!prevButton && !nextButton) return;

    if (this._isWrapAround) {
      [prevButton, nextButton].forEach((button) => {
        if (!button) return;
        button.disabled = false;
        button.removeAttribute('aria-hidden');
      });
      return;
    }

    const viewport = this.slider.querySelector('.flickity-viewport');
    const track = this.slider.querySelector('.flickity-slider');
    const canScroll = Boolean(
      viewport
      && track
      && this.flickityInstance.slides.length > 1
      && track.scrollWidth > viewport.clientWidth + 2
    );

    const { atStart, atEnd } = this._getScrollEdges();

    [prevButton, nextButton].forEach((button) => {
      if (!button) return;
      button.removeAttribute('aria-hidden');
    });

    if (!canScroll) {
      if (prevButton) prevButton.disabled = true;
      if (nextButton) nextButton.disabled = true;
      return;
    }

    if (prevButton) prevButton.disabled = atStart;
    if (nextButton) nextButton.disabled = atEnd;
  }
}

if (!customElements.get('product-related-articles-slider')) {
  customElements.define('product-related-articles-slider', ProductRelatedArticlesSlider);
}
